import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import './StudentDetail.css';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getBackNavigation = () => {
    const from = new URLSearchParams(location.search).get('from');

    if (from === 'school-admin' || authUser?.isSchAdmin) {
      return {
        path: '/school-admin-dashboard',
        text: 'Back to Sch Admin Dashboard',
      };
    }
    if (from === 'org-admin' || authUser?.isOrgAdmin) {
      return {
        path: '/org-admin-dashboard',
        text: 'Back to Org Admin Dashboard',
      };
    }
    if (from === 'teacher') {
      return {
        path: '/teacher-dashboard',
        text: 'Back to Teacher Dashboard',
      };
    }
    return {
      path: '/admin-dashboard',
      text: 'Back to Admin Dashboard',
    };
  };

  const backNav = getBackNavigation();

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(createApiUrl(`/api/student/${id}`), {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        });
        setStudent(response.data);
      } catch (error) {
        console.error('Error fetching student:', error);
        setError('Failed to load student details');
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate(backNav.path)} variant="primary">
          {backNav.text}
        </Button>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">Student not found</Alert>
        <Button onClick={() => navigate(backNav.path)} variant="primary">
          {backNav.text}
        </Button>
      </div>
    );
  }

  return (
    <div className="student-detail-container">
      <div className="container">
        <div className="student-detail-main-content">
          <div className="student-detail-header">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="page-title">Student Details</h2>
              <Button onClick={() => navigate(backNav.path)} className="btn-back">
                {backNav.text}
              </Button>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <Card className="student-detail-card mb-4">
                <Card.Header>
                  <h4>Personal Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Name:</strong>
                        <p>{student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.contactName}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Email:</strong>
                        <p>{student.email}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Student Code:</strong>
                        <p>{student.studentCode}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Year Group:</strong>
                        <p>{student.yearGrade || 'Not set'}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Teacher Student Code:</strong>
                        <p>{student.teacherCode || 'Not assigned'}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Status:</strong>
                        <p><span className={`badge bg-${student.status === 'active' ? 'success' : 'warning'}`}>{student.status}</span></p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="student-detail-card mb-4">
                <Card.Header>
                  <h4>Contact Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Parent Email:</strong>
                        <p>{student.parentEmail || 'Not provided'}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Parent Phone:</strong>
                        <p>{student.parentPhone || 'Not provided'}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Date of Birth:</strong>
                        <p>{student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'Not provided'}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="student-detail-info">
                        <strong>Created:</strong>
                        <p>{new Date(student.createdAt).toLocaleDateString()}</p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="student-detail-card mb-4">
            <Card.Header>
              <h4>Account Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={4}>
                  <div className="student-detail-info">
                    <strong>Username:</strong>
                    <p>{student.username}</p>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="student-detail-info">
                    <strong>Password:</strong>
                    <p>{student.displayPassword || 'Not set'}</p>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="student-detail-info">
                    <strong>Last Updated:</strong>
                    <p>{new Date(student.updatedAt).toLocaleDateString()}</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StudentDetail; 